import React, { useEffect, useState } from "react";
import { Card, Typography, Button, Box } from "@mui/material";
import { fetchCurrentTimeApi, isExamSubmittedByHead } from "../../../services/service";
import { useNavigate } from "react-router-dom";
import ROUTES_NAME from "../../../constants/RoutesName";
import { toast } from "react-toastify";

export default function ExamCard({
  exam,
  handleClick,
  handleInstructionsClick,
  currentTime,
  isPrevious
}) {
  const [disabled, setDisabled] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);
  const navigate = useNavigate();

  const checkDisabled = async () => {
    if (!exam.start_date || !exam.end_time || !currentTime) return false;

    const [year, month, day] = exam.start_date.split("-").map(Number);
    const [hours, minutes, seconds] = exam.end_time.split(":").map(Number);

    const endDateTime = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      seconds
    );

    return currentTime >= endDateTime;
  };

  useEffect(() => {
    const fetchAndSet = async () => {
      const result = await checkDisabled();
      setDisabled(result);
    };

    fetchAndSet();
  }, [currentTime]);

  const handleViewResult = () => {
    navigate(ROUTES_NAME.EXAM_RESULT, {
      state: { examId: exam.id },
    });
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        p: 1,
        maxWidth: "100%",
        mb: { xs: 1, sm: 2 },
      }}
    >
      {/* Outer row: on desktop = flex row (space-between); on mobile = column */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: "row",
          gap: { xs: 1, sm: 2 },
          p: { xs: 0.5, sm: 1 },
        }}
      >
        {/* Left: exam name + details */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              paddingLeft: { xs: "8px", sm: "16px" },
              fontSize: { xs: "0.9rem", sm: "1.2rem", md: "1.5rem" },
              fontWeight: "bold",
              wordBreak: "break-word",
              mb: 0.5
            }}
          >
            {exam.exam_name}
          </Typography>

          {/* Info row — wraps */}
          <Box
            sx={{
              paddingLeft: { xs: "8px", sm: "16px" },
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: "4px 8px", sm: "6px 20px" },
            }}
          >
            <Typography sx={{ fontSize: { xs: "11px", sm: "13px", md: "15px" } }}>
              <span style={{ fontWeight: 500 }}>Start Date:</span> {exam.start_date}
            </Typography>
            <Typography sx={{ fontSize: { xs: "11px", sm: "13px", md: "15px" } }}>
              <span style={{ fontWeight: 500 }}>Start Time:</span> {exam.start_time}
            </Typography>
            <Typography sx={{ fontSize: { xs: "11px", sm: "13px", md: "15px" } }}>
              <span style={{ fontWeight: 500 }}>Duration:</span> {exam.duration} min
            </Typography>
          </Box>
        </Box>

        {/* Right: buttons stacked */}
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexDirection: "column",
            padding: { xs: "4px", sm: "10px" },
            justifyContent: "center",
            flexShrink: 0,
            minWidth: { xs: "100px", sm: "140px" }
          }}
        >
          {isPrevious ? (
         <Button
              variant="contained"
              size="small"
              color="secondary"
              disabled={loadingResult}
              sx={{ 
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                py: { xs: 0.5, sm: 1 },
                background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                color: "#ffffff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                }
              }}
              onClick={handleViewResult}
            >
              {loadingResult ? "Loading..." : "View Result"}
            </Button>
          ) : exam.status === "submitted" ? (
            <Typography style={{ color: "green", fontWeight: 600, textAlign: 'center' }}>
              Completed
            </Typography>
          ) : (
            <>
              <Button
                variant="contained"
                size="small"
                disabled={disabled}
                sx={{ 
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  py: { xs: 0.5, sm: 1 }
                }}
                onClick={(event) =>
                  handleClick(
                    event,
                    exam.id,
                    exam.start_date,
                    exam.start_time,
                    exam.exam_pin,
                    exam.exam_type,
                    exam.before_instruction
                  )
                }
              >
                {exam.status === "resume" ? "Resume" : "Start"}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                sx={{ 
                  fontSize: { xs: "0.6rem", sm: "0.75rem" },
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 0.5, sm: 2 }
                }}
                onClick={() => handleInstructionsClick(exam.id)}
              >
                Instructions
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
}
